// src/modules/app-views/services/pages/Document/tabs/ParametersTab/modals/ReferenceModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input, Select } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ReferenceModal = ({ open, onCancel, onSave, initialData, loading }) => {
  // ---------------------------------------------------------------------------
  // 1. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 2. LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_reference') : translateX('new_reference');
  const typeOptions = [
    { label: 'Video', value: 'video' },
    { label: 'Website', value: 'website' },
  ];

  // ---------------------------------------------------------------------------
  // 3. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          type: initialData.type || 'video',
          title: initialData.title || '',
          url: initialData.url || '',
          channel: initialData.channel || '',
          description: initialData.description || '',
          topic: initialData.topic || '',
        });
      } else {
        form.resetFields();
      }

      // auto-focus on title field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ---------------------------------------------------------------------------
  // 4. HANDLERS
  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const type = values.type || 'video';

      const payload = {
        type,
        title: (values.title || '').trim(),
        url: (values.url || '').trim(),
      };

      // include type-specific fields
      if (type === 'video') {
        payload.channel = (values.channel || '').trim();
        payload.topic = (values.topic || '').trim();
      } else {
        payload.description = (values.description || '').trim();
      }

      onSave(payload);
    } catch (error) {
      // validation failed - form will show errors
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  // ---------------------------------------------------------------------------
  // 5. UI
  // ---------------------------------------------------------------------------
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      getContainer={document.body}
      width={480}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: 'video',
          title: '',
          url: '',
          channel: '',
          description: '',
          topic: '',
        }}
      >
        <Form.Item
          name="type"
          label={translateX('type')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Select
            options={typeOptions}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="title"
          label={translateX('title')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Input
            ref={inputRef}
            maxLength={120}
            placeholder={translateX('title')}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="url"
          label="URL"
        >
          <Input
            placeholder="https://..."
            disabled={loading}
          />
        </Form.Item>

        {/* conditional fields based on type */}
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
          {({ getFieldValue }) => {
            const selectedType = getFieldValue('type');

            if (selectedType === 'video') {
              return (
                <>
                  <Form.Item
                    name="channel"
                    label={translateX('channel')}
                  >
                    <Input
                      maxLength={60}
                      placeholder={translateX('channel')}
                      disabled={loading}
                    />
                  </Form.Item>

                  <Form.Item
                    name="topic"
                    label={translateX('topic')}
                  >
                    <Input
                      placeholder={translateX('topic')}
                      disabled={loading}
                    />
                  </Form.Item>
                </>
              );
            }

            if (selectedType === 'website') {
              return (
                <Form.Item
                  name="description"
                  label={translateX('description')}
                >
                  <Input.TextArea
                    rows={2}
                    maxLength={500}
                    showCount
                    placeholder={translateX('description')}
                    disabled={loading}
                  />
                </Form.Item>
              );
            }

            return null;
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ReferenceModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
